import { Component } from '@theme/component';
import { ThemeEvents, SlideshowSelectEvent, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  /** @type {IntersectionObserver | undefined} */
  #videoObserver;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect, { signal });

    // Custom elements can connect before the parser has added their children.
    requestAnimationFrame(() => {
      if (!this.isConnected) return;
      this.#observeVideos();
      this.#playInitiallySelectedVideo();
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#videoObserver?.disconnect();
  }

  /**
   * Loads grid videos only once they are visible, then pauses them when they
   * leave the viewport. Carousel videos are started from their slideshow
   * selection event so they never play while their slide is hidden.
   */
  #observeVideos() {
    if (!('IntersectionObserver' in window)) return;

    this.#videoObserver = new IntersectionObserver(this.#handleVideoIntersection);

    this.querySelectorAll('.media-gallery__grid deferred-media').forEach((media) => this.#videoObserver?.observe(media));
  }

  /**
   * @param {IntersectionObserverEntry[]} entries
   */
  #handleVideoIntersection = (entries) => {
    entries.forEach((entry) => {
      /** @type {HTMLElement & { loadContent: (focus?: boolean) => void, playMedia: () => void, pauseMedia: () => void }} */
      const media = entry.target;

      if (entry.isIntersecting) {
        this.#playVideo(media);
      } else {
        media.pauseMedia();
      }
    });
  };

  /**
   * Starts a carousel video only after its active slide has been painted.
   *
   * @param {SlideshowSelectEvent} event
   */
  #handleSlideshowSelect = (event) => {
    this.#playCarouselVideo(event.detail.slide, event.detail.userInitiated);
  };

  #playInitiallySelectedVideo() {
    this.#playCarouselVideo(this.querySelector("slideshow-slide[aria-hidden='false']"));
  }

  /**
   * @param {Element | null | undefined} slide
   */
  #playCarouselVideo(slide, userInitiated = false) {
    const media = slide?.querySelector('deferred-media');
    if (!media) return;

    this.#pauseInactiveCarouselVideos(media);

    // Safari's autoplay policy treats content-visibility:hidden media as
    // off-screen, even while a scroll-snap carousel is transitioning to it.
    slide.style.contentVisibility = 'visible';

    // Safari retains user activation only during the event that selected the
    // slide. Start immediately when possible, then retry after paint below.
    if (userInitiated) {
      /** @type {HTMLElement & { loadContent: (focus?: boolean, notify?: boolean) => void, playMedia: () => void }} */
      const video = media;
      this.#playVideo(video, true);
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (slide.getAttribute('aria-hidden') !== 'false') return;

        /** @type {HTMLElement & { loadContent: (focus?: boolean, notify?: boolean) => void, playMedia: () => void }} */
        const video = media;
        this.#playVideo(video, !userInitiated);
      });
    });
  }

  /**
   * @param {Element} activeMedia
   */
  #pauseInactiveCarouselVideos(activeMedia) {
    this.querySelectorAll('slideshow-slide deferred-media').forEach((media) => {
      if (media === activeMedia) return;

      /** @type {HTMLElement & { pauseMedia: () => void }} */
      const inactiveMedia = media;
      inactiveMedia.pauseMedia();

      const video = media.querySelector('video');
      if (video) video.currentTime = 0;
    });
  }

  /**
   * @param {HTMLElement & { loadContent: (focus?: boolean, notify?: boolean) => void, playMedia: (restart?: boolean) => void }} media
   * @param {boolean} [restart]
   */
  #playVideo(media, restart = false) {
    // Visible gallery videos should be able to play together in a grid.
    // Manual video interaction continues to pause other media as before.
    media.loadContent(false, false);
    media.playMedia(restart);
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
